import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { NavLink, useLocation } from 'react-router-dom';
import Logo from '../../images/logo/logo.png';
import { BiSolidDashboard } from "react-icons/bi";
import { FaAddressBook, FaBars, FaCalendarAlt, FaCogs, FaLandmark, FaStickyNote, FaTimes, FaSun, FaQuestionCircle, FaHeadset, FaLayerGroup, FaLeaf, FaFileInvoiceDollar, FaMoneyBill } from 'react-icons/fa';
import { FaUserClock } from 'react-icons/fa';



interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse?: () => void;
}

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse }: SidebarProps) => {
  const location = useLocation();
  const { pathname } = location;
  const [sidebarHover, setSidebarHover] = useState(false);
  const [leadsSubmenuOpen, setLeadsSubmenuOpen] = useState(pathname.includes('/leads') || pathname.includes('/contacts'));
  const [financeSubmenuOpen, setFinanceSubmenuOpen] = useState(pathname.includes('/invoices') || pathname.includes('/payments'));
  const [scheduleSubmenuOpen, setScheduleSubmenuOpen] = useState(pathname.includes('/schedules') || pathname.includes('/notes'));
  const [plotsSubmenuOpen, setPlotsSubmenuOpen] = useState(pathname.includes('/plots') || pathname.includes('/crops'));
  const sidebarRef = useRef<HTMLElement>(null);
  const isMobile = window.innerWidth < 1024; // lg breakpoint

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isOpen, onClose]);

  // Handle keyboard events and body classes
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    // Toggle body classes
    if (isCollapsed) {
      document.body.classList.add('sidebar-collapsed');
      document.body.classList.remove('sidebar-expanded');
    } else {
      document.body.classList.remove('sidebar-collapsed');
      document.body.classList.add('sidebar-expanded');
    }

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Clean up body classes on unmount
      document.body.classList.remove('sidebar-collapsed', 'sidebar-expanded');
    };
  }, [isCollapsed, isOpen, onClose]);

  // Function to render menu items with proper icon and text
  const renderMenuItem = (to: string, icon: React.ReactNode, text: string, pathMatch: string, dataCy?: string) => (
    <li>
      <NavLink
        to={to}
        data-cy={dataCy}
        className={`group relative flex items-center gap-2.5 rounded-md py-2.5 px-2 text-sm font-medium text-white/90 duration-200 ease-in-out 
          hover:bg-primary-darker/80 hover:text-white active:bg-primary-darker transition-colors ${
          pathname.includes(pathMatch) ? 'bg-primary-darker/90 text-white shadow-md' : ''
        }`}
        title={isCollapsed ? text : ''}
      >
        <span className="flex items-center justify-center w-7 text-xl">
          {icon}
        </span>
        {!isCollapsed && <span>{text}</span>}
      </NavLink>
    </li>
  );


  return (
    <aside
      ref={sidebarRef}
      onMouseEnter={() => !isMobile && setSidebarHover(true)}
      onMouseLeave={() => !isMobile && setSidebarHover(false)}
      className={`fixed left-0 top-0 z-50 flex h-screen flex-col overflow-y-auto bg-primary dark:bg-primary-darker duration-300 ease-linear shadow-lg transition-all ${
        isMobile 
          ? (isOpen ? 'translate-x-0' : '-translate-x-full') 
          : 'translate-x-0'
      } ${isCollapsed ? 'w-20' : 'w-72.5'} ${isCollapsed && sidebarHover ? 'shadow-xl' : ''}`}
      style={ {
        transition: 'width 0.3s ease, transform 0.3s ease',
      }}
    >
      {/* <!-- SIDEBAR HEADER -->
          Collapsed: no room for a logo next to a separate expand button
          without both feeling cramped, so the logo itself becomes the
          expand control (and is sized up to actually fill the rail,
          rather than sitting small inside a mostly-empty row). Expanded:
          logo + wordmark, with a small collapse chevron on the far side —
          no "MENU" label; the nav items below make the section obvious
          on their own. */}
      <div className="flex h-16 items-center justify-between gap-2 px-4 bg-primary-darker/80">
        {isCollapsed ? (
          <button
            onClick={onToggleCollapse}
            aria-label="Expand sidebar"
            title="Expand sidebar"
            className="flex w-full items-center justify-center rounded-lg py-1 transition-colors hover:bg-white/10"
          >
            <img
              src={Logo}
              alt="Re:Loam Logo"
              className="h-11 w-11 transition-all duration-300"
              style={ { objectFit: 'contain' }}
            />
          </button>
        ) : (
          <>
            <NavLink to="/dashboard" className="flex items-center rounded-lg transition-all duration-200 group">
              <img
                src={Logo}
                alt="Re:Loam Logo"
                className="h-10 w-10 transition-all duration-300"
                style={ { objectFit: 'contain' }}
              />
              <span className="ml-2.5 text-base font-medium text-white group-hover:text-opacity-90 transition-colors">Re:Loam</span>
            </NavLink>

            {onToggleCollapse && (
              <button
                aria-label="Collapse sidebar"
                onClick={onToggleCollapse}
                className="hidden lg:flex flex-shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 hover:scale-105 active:scale-95"
                title="Collapse sidebar"
              >
                <FaBars className="w-4 h-4 text-white" />
              </button>
            )}

            {/* Close button - only shows on mobile */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-controls="sidebar"
              aria-expanded={isOpen}
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 hover:scale-105 active:scale-95"
              title="Close menu"
            >
              <FaTimes className="w-4 h-4 text-white" />
            </button>
          </>
        )}
      </div>
      {/* <!-- SIDEBAR HEADER --> */}

      <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">

        {/* <!-- Sidebar Menu --> */}
        <nav className="mt-5 py-4 px-4 lg:mt-9 lg:px-6">
          {/* <!-- Menu Group --> */}
          <div className="relative w-full">
            <ul className="mb-6 flex flex-col gap-1.5">

  {/* Menu Item Dashboard */}
  {renderMenuItem(
    "/#",
    <BiSolidDashboard className="text-lg" />,
    "Dashboard",
    "#"
  )}

        {/* Menu Item Leads with Submenu */}
        <li>
          {(() => {
            const [isSubmenuOpen, setIsSubmenuOpen] = [leadsSubmenuOpen, setLeadsSubmenuOpen];
            const toggleSubmenu = (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setIsSubmenuOpen(!isSubmenuOpen);
            };
            
            return (
              <div className="space-y-1">
                {/* Parent Leads Container with Link and Toggle */}
                <div className="relative">
                  <NavLink
                    to="/leads"
                    className={`group relative flex items-center gap-2.5 rounded-md py-2.5 px-2 text-sm font-medium text-white/90 duration-200 ease-in-out 
                      hover:bg-primary-darker/80 hover:text-white active:bg-primary-darker transition-colors ${
                      pathname.includes('/leads') ? 'bg-primary-darker/90 text-white shadow-md' : ''
                    } ${!isCollapsed ? 'pr-8' : ''}`}
                    title={isCollapsed ? 'Leads' : ''}
                  >
                    <span className="flex items-center justify-center w-7 text-xl">
                      <svg
                        className="fill-current"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.10322 0.956299H2.53135C1.5751 0.956299 0.787598 1.7438 0.787598 2.70005V6.27192C0.787598 7.22817 1.5751 8.01567 2.53135 8.01567H6.10322C7.05947 8.01567 7.84697 7.22817 7.84697 6.27192V2.72817C7.8751 1.7438 7.0876 0.956299 6.10322 0.956299ZM6.60947 6.30005C6.60947 6.5813 6.38447 6.8063 6.10322 6.8063H2.53135C2.2501 6.8063 2.0251 6.5813 2.0251 6.30005V2.72817C2.0251 2.44692 2.2501 2.22192 2.53135 2.22192H6.10322C6.38447 2.22192 6.60947 2.44692 6.60947 2.72817V6.30005Z"
                          fill="currentColor"
                        />
                        <path
                          d="M15.4689 0.956299H11.8971C10.9408 0.956299 10.1533 1.7438 10.1533 2.70005V6.27192C10.1533 7.22817 10.9408 8.01567 11.8971 8.01567H15.4689C16.4252 8.01567 17.2127 7.22817 17.2127 6.27192V2.72817C17.2127 1.7438 16.4252 0.956299 15.4689 0.956299ZM15.9752 6.30005C15.9752 6.5813 15.7502 6.8063 15.4689 6.8063H11.8971C11.6158 6.8063 11.3908 6.5813 11.3908 6.30005V2.72817C11.3908 2.44692 11.6158 2.22192 11.8971 2.22192H15.4689C15.7502 2.22192 15.9752 2.44692 15.9752 2.72817V6.30005Z"
                          fill="currentColor"
                        />
                        <path
                          d="M6.10322 9.92822H2.53135C1.5751 9.92822 0.787598 10.7157 0.787598 11.672V15.2438C0.787598 16.2001 1.5751 16.9876 2.53135 16.9876H6.10322C7.05947 16.9876 7.84697 16.2001 7.84697 15.2438V11.7001C7.8751 10.7157 7.0876 9.92822 6.10322 9.92822ZM6.60947 15.272C6.60947 15.5532 6.38447 15.7782 6.10322 15.7782H2.53135C2.2501 15.7782 2.0251 15.5532 2.0251 15.272V11.7001C2.0251 11.4188 2.2501 11.1938 2.53135 11.1938H6.10322C6.38447 11.1938 6.60947 11.4188 6.60947 11.7001V15.272Z"
                          fill="currentColor"
                        />
                        <path
                          d="M15.4689 9.92822H11.8971C10.9408 9.92822 10.1533 10.7157 10.1533 11.672V15.2438C10.1533 16.2001 10.9408 16.9876 11.8971 16.9876H15.4689C16.4252 16.9876 17.2127 16.2001 17.2127 15.2438V11.7001C17.2127 10.7157 16.4252 9.92822 15.4689 9.92822ZM15.9752 15.272C15.9752 15.5532 15.7502 15.7782 15.4689 15.7782H11.8971C11.6158 15.7782 11.3908 15.5532 11.3908 15.272V11.7001C11.3908 11.4188 11.6158 11.1938 11.8971 11.1938H15.4689C15.7502 11.1938 15.9752 11.4188 15.9752 11.7001V15.272Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    {!isCollapsed && <span>Leads</span>}
                  </NavLink>
                  
                  {/* Toggle Button */}
                  {!isCollapsed && (
                    <button
                      onClick={toggleSubmenu}
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 p-3 lg:right-2 lg:p-1 rounded hover:bg-white/10 transition-colors"
                      title={isSubmenuOpen ? 'Collapse submenu' : 'Expand submenu'}
                    >
                      <svg
                        className={`w-4 h-4 text-white transition-transform duration-200 ${isSubmenuOpen ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Submenu Items */}
                {!isCollapsed && isSubmenuOpen && (
                  <ul className="ml-6 space-y-1">
                    <li>
                      <NavLink
                        to="/contacts"
                        data-cy="nav-contacts"
                        className={`group relative flex items-center gap-2.5 rounded-md py-2 px-2 text-sm font-medium text-white/80 duration-200 ease-in-out 
                          hover:bg-primary-darker/60 hover:text-white ${
                            pathname === '/contacts' ? 'bg-primary-darker/70 text-white' : ''
                          }`}
                      >
                        <FaAddressBook className="mr-2" />
                        Contacts
                      </NavLink>
                    </li>
                  </ul>
                )}
              </div>
            );
          })()}
        </li>
        {/* <!-- Menu Item Leads with Submenu --> */}

  {/* Menu Item Contracts */}
  {renderMenuItem(
    "/contracts",
    <FaLandmark className="text-lg" />,
    "Contracts",
    "contracts"
  )}
  {/* <!-- Menu Item Contracts --> */}

  {/* Menu Item
  
  
   {/* Menu Item Invoices with Submenu */}
              <li>
          {(() => {
            const [isSubmenuOpen, setIsSubmenuOpen] = [financeSubmenuOpen, setFinanceSubmenuOpen];
            const toggleSubmenu = (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setIsSubmenuOpen(!isSubmenuOpen);
            };
            
            return (
              <div className="space-y-1">
                {/* Parent Invoices Container with Link and Toggle */}
                <div className="relative">
                  <NavLink
                    to="/invoices"
                    className={`group relative flex items-center gap-2.5 rounded-md py-2.5 px-2 text-sm font-medium text-white/90 duration-200 ease-in-out 
                      hover:bg-primary-darker/80 hover:text-white active:bg-primary-darker transition-colors ${
                      pathname.includes('/invoice') ? 'bg-primary-darker/90 text-white shadow-md' : ''
                    } ${!isCollapsed ? 'pr-8' : ''}`}
                    title={isCollapsed ? 'Invoice' : ''}
                  >
                    <span className="flex items-center justify-center w-7 text-xl">
                      <FaCalendarAlt className="text-lg" />
                    </span>
                    {!isCollapsed && <span>Invoices</span>}
                  </NavLink>
                  
                  {/* Toggle Button */}
                  {!isCollapsed && (
                    <button
                      onClick={toggleSubmenu}
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 p-3 lg:right-2 lg:p-1 rounded hover:bg-white/10 transition-colors"
                      title={isSubmenuOpen ? 'Collapse submenu' : 'Expand submenu'}
                    >
                      <svg
                        className={`w-4 h-4 text-white transition-transform duration-200 ${isSubmenuOpen ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Submenu Items */}
                {!isCollapsed && isSubmenuOpen && (
                  <ul className="ml-6 space-y-1">
                    <li>
                      <NavLink
                        to="/invoices/arrears"
                        className={`group relative flex items-center gap-2.5 rounded-md py-2 px-2 text-sm font-medium text-white/80 duration-200 ease-in-out 
                          hover:bg-primary-darker/60 hover:text-white ${
                            pathname === '/invoices/arrears' ? 'bg-primary-darker/70 text-white' : ''
                          }`}
                      >
                        <FaUserClock className="mr-2" />
                        Arrears
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/payments"
                        className={`group relative flex items-center gap-2.5 rounded-md py-2 px-2 text-sm font-medium text-white/80 duration-200 ease-in-out 
                          hover:bg-primary-darker/60 hover:text-white ${
                            pathname === '/payments' ? 'bg-primary-darker/70 text-white' : ''
                          }`}
                      >
                        <FaMoneyBill className="mr-2" />
                        Payments
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/tax-reporting"
                        className={`group relative flex items-center gap-2.5 rounded-md py-2 px-2 text-sm font-medium text-white/80 duration-200 ease-in-out 
                          hover:bg-primary-darker/60 hover:text-white ${
                            pathname === '/tax-reporting' ? 'bg-primary-darker/70 text-white' : ''
                          }`}
                      >
                        <FaFileInvoiceDollar className="mr-2" />
                        Tax Reports
                      </NavLink>
                    </li>
                  </ul>
                )}
              </div>
            );
          })()}
        </li>
        {/* <!-- Menu Item Invoices with Submenu --> */}







    {/* Menu Item Proposals */}
    {renderMenuItem(
      "/proposals",
      <svg
        className="fill-current"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.43425 7.5093H2.278C2.44675 7.5093 2.55925 7.3968 2.58737 7.31243L2.98112 6.32805H5.90612L6.27175 7.31243C6.328 7.48118 6.46862 7.5093 6.58112 7.5093H7.453C7.76237 7.48118 7.87487 7.25618 7.76237 7.03118L5.428 1.4343C5.37175 1.26555 5.3155 1.23743 5.14675 1.23743H3.88112C3.76862 1.23743 3.59987 1.29368 3.57175 1.4343L1.153 7.08743C1.0405 7.2843 1.20925 7.5093 1.43425 7.5093ZM4.47175 2.98118L5.3155 5.17493H3.59987L4.47175 2.98118Z"
          fill="currentColor"
        />
        <path
          d="M10.1249 2.5031H16.8749C17.2124 2.5031 17.5218 2.22185 17.5218 1.85623C17.5218 1.4906 17.2405 1.20935 16.8749 1.20935H10.1249C9.7874 1.20935 9.47803 1.4906 9.47803 1.85623C9.47803 2.22185 9.75928 2.5031 10.1249 2.5031Z"
          fill="currentColor"
        />
        <path
          d="M16.8749 6.21558H10.1249C9.7874 6.21558 9.47803 6.49683 9.47803 6.86245C9.47803 7.22808 9.75928 7.50933 10.1249 7.50933H16.8749C17.2124 7.50933 17.5218 7.22808 17.5218 6.86245C17.5218 6.49683 17.2124 6.21558 16.8749 6.21558Z"
          fill="currentColor"
        />
        <path
          d="M16.875 11.1656H1.77187C1.43438 11.1656 1.125 11.4469 1.125 11.8125C1.125 12.1781 1.40625 12.4594 1.77187 12.4594H16.875C17.2125 12.4594 17.5219 12.1781 17.5219 11.8125C17.5219 11.4469 17.2125 11.1656 16.875 11.1656Z"
          fill="currentColor"
        />
        <path
          d="M16.875 16.1156H1.77187C1.43438 16.1156 1.125 16.3969 1.125 16.7625C1.125 17.1281 1.40625 17.4094 1.77187 17.4094H16.875C17.2125 17.4094 17.5219 17.1281 17.5219 16.7625C17.5219 16.3969 17.2125 16.1156 16.875 16.1156Z"
          fill="currentColor"
        />
      </svg>,
      "Proposals",
      "proposals"
    )}
     {/* <!-- Menu Item Proposals --> */}


      
              {/* Menu Item Schedules with Submenu */}
              <li>
          {(() => {
            const [isSubmenuOpen, setIsSubmenuOpen] = [scheduleSubmenuOpen, setScheduleSubmenuOpen];
            const toggleSubmenu = (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setIsSubmenuOpen(!isSubmenuOpen);
            };
            
            return (
              <div className="space-y-1">
                {/* Parent Schedules Container with Link and Toggle */}
                <div className="relative">
                  <NavLink
                    to="/schedule"
                    className={`group relative flex items-center gap-2.5 rounded-md py-2.5 px-2 text-sm font-medium text-white/90 duration-200 ease-in-out 
                      hover:bg-primary-darker/80 hover:text-white active:bg-primary-darker transition-colors ${
                      pathname.includes('/schedule') ? 'bg-primary-darker/90 text-white shadow-md' : ''
                    } ${!isCollapsed ? 'pr-8' : ''}`}
                    title={isCollapsed ? 'Schedule' : ''}
                  >
                    <span className="flex items-center justify-center w-7 text-xl">
                      <FaCalendarAlt className="text-lg" />
                    </span>
                    {!isCollapsed && <span>Schedules</span>}
                  </NavLink>
                  
                  {/* Toggle Button */}
                  {!isCollapsed && (
                    <button
                      onClick={toggleSubmenu}
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 p-3 lg:right-2 lg:p-1 rounded hover:bg-white/10 transition-colors"
                      title={isSubmenuOpen ? 'Collapse submenu' : 'Expand submenu'}
                    >
                      <svg
                        className={`w-4 h-4 text-white transition-transform duration-200 ${isSubmenuOpen ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Submenu Items */}
                {!isCollapsed && isSubmenuOpen && (
                  <ul className="ml-6 space-y-1">
                    <li>
                      <NavLink
                        to="/notes"
                        className={`group relative flex items-center gap-2.5 rounded-md py-2 px-2 text-sm font-medium text-white/80 duration-200 ease-in-out 
                          hover:bg-primary-darker/60 hover:text-white ${
                            pathname === '/notes' ? 'bg-primary-darker/70 text-white' : ''
                          }`}
                      >
                        <FaStickyNote className="mr-2" />
                        Notes
                      </NavLink>
                    </li>
                  </ul>
                )}
              </div>
            );
          })()}
        </li>
        {/* <!-- Menu Item Schedules with Submenu --> */}




        {/* Menu Item Plots with Submenu */}
        <li>
          {(() => {
            const [isSubmenuOpen, setIsSubmenuOpen] = [plotsSubmenuOpen, setPlotsSubmenuOpen];
            const toggleSubmenu = (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setIsSubmenuOpen(!isSubmenuOpen);
            };
            
            return (
              <div className="space-y-1">
                {/* Parent Plots Container with Link and Toggle */}
                <div className="relative">
                  <NavLink
                    to="/plots"
                    className={`group relative flex items-center gap-2.5 rounded-md py-2.5 px-2 text-sm font-medium text-white/90 duration-200 ease-in-out 
                      hover:bg-primary-darker/80 hover:text-white active:bg-primary-darker transition-colors ${
                      pathname.includes('/plots') ? 'bg-primary-darker/90 text-white shadow-md' : ''
                    } ${!isCollapsed ? 'pr-8' : ''}`}
                    title={isCollapsed ? 'Plots' : ''}
                  >
                    <span className="flex items-center justify-center w-7 text-xl">
                      <FaLayerGroup className="text-lg" />
                    </span>
                    {!isCollapsed && <span>Plots</span>}
                  </NavLink>
                  
                  {/* Toggle Button */}
                  {!isCollapsed && (
                    <button
                      onClick={toggleSubmenu}
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 p-3 lg:right-2 lg:p-1 rounded hover:bg-white/10 transition-colors"
                      title={isSubmenuOpen ? 'Collapse submenu' : 'Expand submenu'}
                    >
                      <svg
                        className={`w-4 h-4 text-white transition-transform duration-200 ${isSubmenuOpen ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Submenu Items */}
                {!isCollapsed && isSubmenuOpen && (
                  <ul className="ml-6 space-y-1">
                    <li>
                      <NavLink
                        to="/crops"
                        className={`group relative flex items-center gap-2.5 rounded-md py-2 px-2 text-sm font-medium text-white/80 duration-200 ease-in-out 
                          hover:bg-primary-darker/60 hover:text-white ${
                            pathname === '/crops' ? 'bg-primary-darker/70 text-white' : ''
                          }`}
                      >
                        <FaLeaf className="mr-2" />
                        Crops
                      </NavLink>
                    </li>
                  </ul>
                )}
              </div>
            );
          })()}
        </li>
        {/* <!-- Menu Item Plots with Submenu --> */}

           {/* Menu Item Settings */}
    {renderMenuItem(
          "/settings",
          <FaCogs className="text-lg" />,
          "Settings",
          "settings"
        )}
        {/* <!-- Menu Item Settings --> */}



       
            </ul>
          </div>

        </nav>
        {/* <!-- Sidebar Menu --> */}
      </div>
      
      {/* Footer Section */}
      <div className="mt-auto">
        {/* Collapsed Time Display */}
        {isCollapsed ? (
          <div className="p-2">
            <div className="relative h-14 w-14 mx-auto flex items-center justify-center rounded-lg bg-white/5 backdrop-blur-sm border border-white/10">
              <div className="text-center">
                <div className="text-xs font-medium text-white">
                  {format(new Date(), 'h')}
                  <span className="text-[10px] opacity-60">{format(new Date(), 'a')}</span>
                </div>
                <div className="text-[10px] text-white/60">
                  {format(new Date(), 'MMM d')}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4">
            <div className="relative overflow-hidden rounded-xl border border-white/10 shadow-xl">
              {/* Background Image with Glass Effect */}
              <div 
                className="absolute inset-0 bg-cover bg-center z-0 transform scale-110"
                style={ {
                  backgroundImage: 'url(https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80)',
                  filter: 'blur(2px) brightness(0.7)',
                  transform: 'scale(1.1)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/30 to-black/20 backdrop-blur-sm"></div>
              </div>
              
              {/* Frosted Glass Overlay */}
              <div className="absolute inset-0 bg-white/5 border-t border-white/10"></div>
              
              {/* Content */}
              <div className="relative flex flex-col p-4 z-10">
                
                
                {/* Time and Date */}
                
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-white">
                      {format(new Date(), 'h:mm')}
                      <span className="text-base font-normal ml-1 text-white/80">{format(new Date(), 'a')}</span>
                    </div>
                    <div className="text-sm text-white/80 text-right">
                      <div>{format(new Date(), 'EEE, MMM d')}</div>
                      <div className="text-xs text-white/70">{format(new Date(), 'yyyy')}</div>
                    </div>
                  </div>
              
                
                {/* Divider */}
                <div className="border-t border-white/20 my-2"></div>
                
                {/* Weather Info */}
                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center">
                    <div className="text-2xl font-bold text-white">24°</div>
                    <div className="ml-2 text-xs text-white/90">
                      <div>Partly Cloudy</div>
                      <div>H:26° L:18°</div>
                    </div>
                  </div>
                  <div className="text-3xl text-yellow-300">
                    <FaSun />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-white/10 mx-4"></div>
        
        {/* Support Button */}
        {isCollapsed ? (
          <button
            className="w-full py-3 flex items-center justify-center text-white/40 cursor-not-allowed"
            disabled
            title="Support coming soon"
            aria-label="Support coming soon"
          >
            <FaQuestionCircle className="text-xl" />
          </button>
        ) : (
          <button
            className="w-full py-3 px-4 flex items-center text-white/40 cursor-not-allowed"
            disabled
            title="Support coming soon"
          >
            <FaHeadset className="mr-3" />
            <span className="text-sm font-medium">Get Support (Coming Soon)</span>
          </button>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;